const asObject = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export const isTransactionAuthorisationNotification = (notification) =>
  String(
    notification?.notificationType ?? notification?.NotificationType ?? ""
  ).toLowerCase() === "transaction_authorisation";

export const normalizeTransactionStatusEvent = (notification) => {
  if (!notification || isTransactionAuthorisationNotification(notification)) {
    return null;
  }

  const data = asObject(notification.data ?? notification.Data);
  const payload = asObject(
    data.payload ?? data.Payload ?? notification.payload ?? notification.Payload
  );
  const action = String(
    firstValue(notification.action, notification.Action, payload.action, payload.Action) ?? ""
  ).toLowerCase();
  const rawStatus = String(
    firstValue(notification.status, notification.Status, payload.status, payload.Status) ?? ""
  ).toLowerCase();
  const isSuccess = firstValue(
    notification.IsSuccess,
    notification.isSuccess,
    payload.IsSuccess,
    payload.isSuccess
  );

  if (!["initiated", "transaction_update", "transactionupdate"].includes(action)) {
    return null;
  }

  let status = "PROCESSING";
  if (["success", "successful", "completed"].includes(rawStatus)) {
    status = "SUCCESSFUL";
  } else if (["failed", "failure", "error"].includes(rawStatus) || isSuccess === false) {
    status = "FAILED";
  } else if (["cancelled", "canceled", "declined", "rejected"].includes(rawStatus)) {
    status = "CANCELLED";
  }

  return {
    action,
    status,
    transactionId: firstValue(
      notification.transactionId,
      notification.TransactionId,
      data.transactionId,
      data.TransactionId,
      payload.transactionId,
      payload.TransactionId
    ),
    requestId: firstValue(
      notification.requestId,
      notification.RequestId,
      data.requestId,
      data.RequestId,
      payload.requestId,
      payload.RequestId
    ),
    message: firstValue(
      notification.message,
      notification.Message,
      payload.message,
      payload.Message,
      data.message,
      data.Message
    ),
  };
};

export const getAgentTransactionDetails = (message) => {
  const component = message?.components?.find(
    (item) => String(item?.type ?? item?.Type).toUpperCase() === "TRANSACTION_CONFIRMATION"
  );
  if (!component) return null;

  const data = asObject(component.data ?? component.Data);
  const authHint = message?.authHint ?? message?.AuthHint ?? {};

  return {
    component,
    transactionId: firstValue(
      data.transactionId,
      data.TransactionId,
      authHint.transactionRef,
      authHint.TransactionRef
    ),
    requestId: firstValue(data.requestId, data.RequestId),
    expiresAt: firstValue(data.expiresAt, data.ExpiresAt),
  };
};

export const transactionEventMatches = (event, transaction) => {
  if (!event || !transaction) return false;

  const eventIds = [event.transactionId, event.requestId]
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map(String);
  const transactionIds = [transaction.transactionId, transaction.requestId]
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map(String);

  return eventIds.some((id) => transactionIds.includes(id));
};

export const transactionRecordStatus = (record) => {
  const numericStatus = Number(record?.transactionStatus ?? record?.TransactionStatus);
  const description = String(
    record?.transactionStatusDescription ??
      record?.TransactionStatusDescription ??
      record?.status ??
      record?.Status ??
      ""
  ).toLowerCase();

  if (numericStatus === 4 || ["success", "successful", "completed"].includes(description)) {
    return "SUCCESSFUL";
  }
  if (numericStatus === 3 || ["failed", "failure", "error"].includes(description)) {
    return "FAILED";
  }
  if (
    [5, 6].includes(numericStatus) ||
    ["cancelled", "canceled", "declined", "rejected"].includes(description)
  ) {
    return "CANCELLED";
  }

  return "PROCESSING";
};

export const isTerminalAgentTransactionStatus = (status) =>
  ["SUCCESSFUL", "FAILED", "CANCELLED", "TIMED_OUT"].includes(status);
